import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAPI } from "../Context/MyContext";
import toast from 'react-hot-toast';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const Ico = {
  Phone: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.95 11.5a19.79 19.79 0 01-3.07-8.67A2 2 0 012.88 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 8.69a16 16 0 006.29 6.29l1.06-1.06a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>,
  Eye: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  Globe: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>,
  Clock: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  Calendar: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  Callback: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>,
  Check: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5"><polyline points="20 6 9 17 4 12" /></svg>,
  X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3 h-3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Plus: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Trash: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>,
};

/* ─── Toggle Switch ──────────────────────────────────────────────────────── */
const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${enabled ? "bg-emerald-500" : "bg-slate-200"
      }`}
  >
    <span className={`inline-block w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-200 mt-0.5 ${enabled ? "translate-x-5" : "translate-x-0.5"
      }`} />
  </button>
);

/* ─── Status Badge ───────────────────────────────────────────────────────── */
const StatusBadge = ({ enabled }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border ${enabled
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-50 text-slate-500 border-slate-200"
    }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

/* ─── Card ───────────────────────────────────────────────────────────────── */
const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${className}`}>
    {children}
  </div>
);

/* ─── Section Header ─────────────────────────────────────────────────────── */
const SectionHeader = ({ icon: Icon, title, sub, badge, toggle, onToggle }) => (
  <div className="flex items-start justify-between p-6 border-b border-slate-100">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500 shrink-0 mt-0.5">
        <Icon />
      </div>
      <div>
        <div className="flex items-center gap-2.5 mb-0.5">
          <h2 className="text-sm font-bold text-slate-900">{title}</h2>
          {badge !== undefined && <StatusBadge enabled={badge} />}
        </div>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
    </div>
    {onToggle !== undefined && (
      <Toggle enabled={badge} onChange={onToggle} />
    )}
  </div>
);

/* ─── Skeleton Components ──────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <Card className="animate-pulse">
    <div className="flex items-start justify-between p-6 border-b border-slate-100">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-slate-200" />
        <div className="space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="h-4 bg-slate-200 rounded w-32" />
            <div className="h-6 bg-slate-200 rounded w-16" />
          </div>
          <div className="h-3 bg-slate-200 rounded w-64" />
        </div>
      </div>
      <div className="w-11 h-6 bg-slate-200 rounded-full" />
    </div>
    <div className="p-6">
      <div className="h-10 bg-slate-100 rounded-xl" />
    </div>
  </Card>
);

const SkeletonPage = () => (
  <div className="space-y-6 max-w-4xl mx-auto">
    {/* Header - Keep visible */}
    <div className="flex items-start justify-between">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Call Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure calling features for this WhatsApp number</p>
      </div>
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border bg-slate-50 text-slate-500 border-slate-200 animate-pulse">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Loading...
      </span>
    </div>

    {/* Cards */}
    <SkeletonCard />

    <div className="space-y-5">
      <SkeletonCard />
      <SkeletonCard />

      {/* Larger card for Call Hours */}
      <Card className="animate-pulse">
        <div className="flex items-start justify-between p-6 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-slate-200" />
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <div className="h-4 bg-slate-200 rounded w-24" />
                <div className="h-6 bg-slate-200 rounded w-16" />
              </div>
              <div className="h-3 bg-slate-200 rounded w-80" />
            </div>
          </div>
          <div className="w-11 h-6 bg-slate-200 rounded-full" />
        </div>
        <div className="p-6 space-y-4">
          <div className="h-10 bg-slate-100 rounded-xl" />
          <div className="space-y-3">
            <div className="h-12 bg-slate-100 rounded-xl" />
            <div className="h-12 bg-slate-100 rounded-xl" />
          </div>
        </div>
      </Card>

      <SkeletonCard />

      {/* Save button skeleton */}
      <div className="flex justify-end pb-4 animate-pulse">
        <div className="h-10 bg-slate-200 rounded-xl w-40" />
      </div>
    </div>
  </div>
);

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmt = (t) => {
  if (!t) return "";
  return `${t.slice(0, 2)}:${t.slice(2)}`;
};

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_SHORT = { MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed", THURSDAY: "Thu", FRIDAY: "Fri", SATURDAY: "Sat", SUNDAY: "Sun" };

const COUNTRY_NAMES = {
  US: "United States", BR: "Brazil", GB: "United Kingdom",
  PK: "Pakistan", IN: "India", DE: "Germany", FR: "France",
  AE: "UAE", SA: "Saudi Arabia", CA: "Canada",
};

/* ════════════════════════════════════════════════════════════════════════════
   Main Page
════════════════════════════════════════════════════════════════════════════ */
const CallSettings = () => {
  const { phoneId } = useParams();
  const { getCallingSettings, updateCallingSettings } = useAPI();

  // const [errorMsg, setErrorMsg] = useState(null);
  const [callingLocked, setCallingLocked] = useState(false);


  const [callingEnabled, setCallingEnabled] = useState([]);
  const [iconVisibility, setIconVisibility] = useState("DEFAULT");
  const [restrictedCountries, setRestrictedCountries] = useState([]);
  const [newCountry, setNewCountry] = useState("");
  const [callHoursEnabled, setCallHoursEnabled] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [callbackEnabled, setCallbackEnabled] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const [loading, setLoading] = useState(true);
  const [highlightWeeklyHours, setHighlightWeeklyHours] = useState(false);

  const enableCallHours = (enabled) => {
    setCallHoursEnabled(enabled);
    if (enabled && weeklyHours.length === 0) {
      // Select Monday by default
      setWeeklyHours([{ day_of_week: "MONDAY", open_time: "0900", close_time: "1700" }]);
    }
  };

  // const interpretCallingError = (err) => {
  //   const data = err?.response?.data || err || {};

  //   const message = data.message || "";
  //   const code = data.code;
  //   const subcode = data.subcode;

  //   // ───── Meta technical prerequisite error ─────
  //   if (code === 138018 || subcode === 2593151) {
  //     return {
  //       message:
  //         "Calling is not available yet. WhatsApp has not completed technical activation for this number.",
  //       lock: true,
  //     };
  //   }

  //   // ───── Messaging tier restriction ─────
  //   if (message.toLowerCase().includes("messaging tier")) {
  //     return {
  //       message:
  //         "Calling will unlock automatically after your WhatsApp messaging limit increases.",
  //       lock: true,
  //     };
  //   }

  //   // fallback
  //   return {
  //     message: message || "Failed to update calling settings.",
  //     lock: false,
  //   };
  // };

 const interpretCallingError = (err) => {
  const message =
    err?.response?.data?.message ||
    err?.message ||
    "";

  const code =
    err?.response?.data?.code ||
    err?.code;

  const subcode =
    err?.response?.data?.subcode ||
    err?.subcode;

  const details =
    err?.response?.data?.details?.details ||
    err?.details?.details ||
    "";

  const msg = (message + " " + details).toLowerCase();

  // ───── Meta activation pending ─────
  if (code === 138018 || subcode === 2593151) {
    return {
      message:
        "Calling is not available yet. WhatsApp is still preparing this number.",
      lock: true,
    };
  }

  // ───── Calling cannot be enabled  ─────
  if (code === 138015 || subcode === 2593145) {
    return {
      message:
        "Calling APIs cannot be enabled for this phone number.",
      lock: true,
    };
  }

  // ───── Messaging tier restriction ─────
  if (msg.includes("messaging tier")) {
    return {
      message:
        "Calling requires messaging tier ≥ 2000. Upgrade your WhatsApp messaging limit to enable calling.",
      lock: true,
    };
  }

  // ───── Ignore schema validation junk ─────
  if (
    msg.includes("invalid request body") ||
    msg.includes("json schema") ||
    msg.includes("enum")
  ) {
    return {
      message: "Calling update failed. Please try again later.",
      lock: false,
    };
  }

  // ───── fallback ─────
  return {
    message: message || "Calling update failed.",
    lock: false,
  };
};


  useEffect(() => {
    if (!phoneId) return;

    const loadCallingSettings = async () => {
      setLoading(true);
      try {
        const res = await getCallingSettings(phoneId);
        const c = res?.calling;
        if (!c) return;

        setCallingEnabled(c.status === "ENABLED");
        setIconVisibility(
          ["DEFAULT", "DISABLE_ALL"].includes(c.call_icon_visibility)
            ? c.call_icon_visibility
            : "DEFAULT"
        );
        setRestrictedCountries(c.call_icons?.restrict_to_user_countries || []);
        setCallbackEnabled(
          c.callback_permission_status === "DEFAULT"
        );

        const ch = c.call_hours;
        setCallHoursEnabled(ch?.status === "ENABLED");
        setTimezone(ch?.timezone_id || "");
        setWeeklyHours(ch?.weekly_operating_hours || []);
        setHolidays(
          (ch?.holiday_schedule || []).map(h => ({
            ...h,
            date: h.date?.split("T")[0]
          }))
        );

      } catch (err) {
        console.error("❌ Failed to load calling settings:", err);
      } finally {
        setLoading(false); // done loading
      }
    };

    loadCallingSettings();
  }, [phoneId]);


  const saveSettings = async () => {
    if (callHoursEnabled && weeklyHours.length === 0) {
      return toast.error("Please select at least one day for weekly operating hours");
    }

    const loadingToast = toast.loading("Updating call settings...");
    try {
      const payload = {
        calling: {
          // status: callingEnabled ? "ENABLED" : "DISABLED",
          call_icon_visibility:
            iconVisibility === "DEFAULT" || iconVisibility === "DISABLE_ALL"
              ? iconVisibility
              : null,
          call_icons: { restrict_to_user_countries: restrictedCountries },
          call_hours: {
            status: callHoursEnabled ? "ENABLED" : "DISABLED",
            timezone_id: timezone || "Asia/Karachi",
            weekly_operating_hours: callHoursEnabled
              ? weeklyHours.map(h => ({
                day_of_week: h.day_of_week,
                open_time: h.open_time,
                close_time: h.close_time,
              }))
              : [],
            ...(callHoursEnabled && {
              holiday_schedule: holidays.map(h => ({
                date: h.date?.split("T")[0],
                start_time: h.start_time,
                end_time: h.end_time,
              })),
            }),
          },
          callback_permission_status: callbackEnabled ? "ENABLED" : "DISABLED",
        },
      };

      await updateCallingSettings(phoneId, payload);
      toast.dismiss(loadingToast);
      toast.success("Call settings updated successfully");
    } catch (err) {
      console.error("Failed:", err);
      toast.dismiss(loadingToast);

      const result = interpretCallingError(err);

      // revert toggle UX
      setCallingEnabled(false);

      if (result.lock) setCallingLocked(true);

      toast.error(result.message || "Failed to update call settings");
    }
  };

  /* ── Helpers ── */
  const toggleDay = (day) => {
    setWeeklyHours(prev =>
      prev.find(h => h.day_of_week === day)
        ? prev.filter(h => h.day_of_week !== day)
        : [...prev, { day_of_week: day, open_time: "0900", close_time: "1700" }]
    );
  };

  const updateHour = (day, field, val) => {
    setWeeklyHours(prev => prev.map(h =>
      h.day_of_week === day ? { ...h, [field]: val.replace(":", "") } : h
    ));
  };

  // const addHoliday = () => setHolidays(prev => [...prev, { date: "", start_time: "0000", end_time: "2359" }]);
  const addHoliday = () => {
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    setHolidays(prev => [...prev, { date: today, start_time: "09:00", end_time: "17:00" }]);
  };

  const removeHoliday = (i) => setHolidays(prev => prev.filter((_, idx) => idx !== i));
  // const updateHoliday = (i, field, val) => setHolidays(prev => prev.map((h, idx) => idx === i ? { ...h, [field]: val } : h));
  const updateHoliday = (i, field, val) =>
    setHolidays(prev =>
      prev.map((h, idx) => idx === i ? { ...h, [field]: val } : h)
    );

  const addCountry = () => {
    const code = newCountry.toUpperCase().trim();
    if (code && !restrictedCountries.includes(code)) {
      setRestrictedCountries(prev => [...prev, code]);
      setNewCountry("");
    }
  };

  if (loading) {
    return <SkeletonPage />;
  }


  const handleCallingToggle = async (enabled) => {
  if (callingLocked) return;

  setCallingEnabled(enabled); // update UI immediately

  const loadingToast = toast.loading(
    enabled ? "Enabling calling..." : "Disabling calling..."
  );

  try {
    const payload = {
      calling: {
        status: enabled ? "ENABLED" : "DISABLED",
      },
    };

    await updateCallingSettings(phoneId, payload);

    toast.dismiss(loadingToast);
    toast.success(enabled ? "Calling enabled" : "Calling disabled");
  } catch (err) {
    toast.dismiss(loadingToast);

    const result = interpretCallingError(err);

    // revert UI if API fails
    setCallingEnabled(!enabled);

    if (result.lock) setCallingLocked(true);

    toast.error(result.message || "Failed to update calling status");
  }
};


  return (
    <div className="space-y-6 max-w-4xl mx-auto">

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Call Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Configure calling features for this WhatsApp number</p>
        </div>
        <StatusBadge enabled={callingEnabled} />
      </div>

      {/* {errorMsg && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-3">
          ⚠ {errorMsg}
        </div>
      )} */}

      {/* ── 1. Master Calling Toggle ── */}
      <Card>
        <SectionHeader
          icon={Ico.Phone}
          title="Calling"
          sub="Enable or disable calling for this WhatsApp number"
          badge={callingEnabled}
          onToggle={handleCallingToggle}
        />
        {callingLocked && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              🔒 Calling is locked by WhatsApp and will become available automatically once requirements are met.
            </p>
          </div>
        )}
      </Card>

      <div className={`space-y-5 ${!callingEnabled ? "opacity-40 pointer-events-none" : ""}`}>

        {/* ── 2. Call Icon Visibility ── */}
        <Card>
          <SectionHeader
            icon={Ico.Eye}
            title="Call Icon Visibility"
            sub="Control whether the call icon appears in the WhatsApp chat interface"
          // badge={iconVisibility === "DEFAULT"}
          />
          <div className="p-6">
            <div className="flex gap-3">
              {["DEFAULT", "DISABLE_ALL"].map(opt => (
                <button
                  key={opt}
                  onClick={() => setIconVisibility(opt)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${iconVisibility === opt
                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                    : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                >
                  {/* {opt === "DEFAULT" ? "Show (Default)" : "Hide"} */}
                  {opt === "DEFAULT" ? "Show (Default)" : "Disable All"}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">
              {iconVisibility === "DEFAULT"
                ? "Call icon is visible to users in the chat interface."
                : "Call icon is hidden — users cannot initiate calls."}
            </p>
          </div>
        </Card>

        {/* ── 3. Country Restrictions ── */}
        <Card>
          <SectionHeader
            icon={Ico.Globe}
            title="Restrict Calls to Countries"
            sub="Only allow calls from users in the selected countries"
          />
          <div className="p-6 space-y-4">
            {/* Country tags */}
            <div className="flex flex-wrap gap-2">
              {restrictedCountries.map(code => (
                <div key={code} className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg px-3 py-1.5 text-xs font-semibold">
                  <span>{code}</span>
                  {COUNTRY_NAMES[code] && <span className="text-indigo-400 font-normal">· {COUNTRY_NAMES[code]}</span>}
                  <button onClick={() => setRestrictedCountries(p => p.filter(c => c !== code))}
                    className="ml-1 text-indigo-400 hover:text-indigo-700 transition-colors">
                    <Ico.X />
                  </button>
                </div>
              ))}
              {restrictedCountries.length === 0 && (
                <p className="text-xs text-slate-400">No restrictions calls allowed from all countries.</p>
              )}
            </div>

            {/* Add country */}
            <div className="flex gap-2">
              <input
                type="text"
                maxLength={2}
                value={newCountry}
                onChange={e => setNewCountry(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && addCountry()}
                placeholder="Country code (e.g. US)"
                className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent placeholder-slate-300"
              />
              <button onClick={addCountry}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl transition-colors">
                <Ico.Plus /> Add
              </button>
            </div>
          </div>
        </Card>

        {/* ── 5. Callback Permission ── */}
        <Card>
          <SectionHeader
            icon={Ico.Callback}
            title="Callback Permission"
            sub="Allow users to request a callback when you're unavailable or outside call hours"
            badge={callbackEnabled}
            onToggle={setCallbackEnabled}
          />
          <div className="px-6 py-4">
            <div className={`rounded-xl border px-4 py-3 text-xs leading-relaxed ${callbackEnabled
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-slate-50 border-slate-200 text-slate-500"
              }`}>
              {callbackEnabled
                ? "✓ Users can request a callback. They will see a callback option when you are unavailable."
                : "Callback requests are disabled. Users will not see the callback option."}
            </div>
          </div>
        </Card>

        {/* ── 4. Call Hours ── */}
        <Card>
          <SectionHeader
            icon={Ico.Clock}
            title="Call Hours"
            sub="Set when this number accepts incoming calls each week"
            badge={callHoursEnabled}
            onToggle={setCallHoursEnabled}
          />

          <div className={`${!callHoursEnabled ? "opacity-40 pointer-events-none" : ""}`}>

            {/* Timezone */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest w-24 shrink-0">Timezone</p>
              <select
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
                className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white text-slate-700"
              >
                {["America/Manaus", "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Karachi", "Asia/Dubai", "Asia/Kolkata"].map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            {/* Weekly Hours */}
            <div className="px-6 py-5 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4">Weekly Operating Hours</p>

              {/* Day toggles */}
              <div className="flex gap-2 flex-wrap mb-5">
                {DAYS.map(day => {
                  const active = weeklyHours.some(h => h.day_of_week === day);
                  return (
                    <button key={day} onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${active
                        ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                        : "bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-300"
                        }`}>
                      {DAY_SHORT[day]}
                    </button>
                  );
                })}
              </div>

              {/* Hours per active day */}
              <div className="space-y-3">
                {DAYS.filter(d => weeklyHours.some(h => h.day_of_week === d)).map(day => {
                  const h = weeklyHours.find(x => x.day_of_week === day);
                  return (
                    <div key={day} className="flex items-center gap-4 bg-slate-50 rounded-xl px-4 py-3">
                      <span className="text-xs font-bold text-slate-600 w-10 shrink-0">{DAY_SHORT[day]}</span>
                      <div className="flex items-center gap-2 flex-1">
                        <label className="text-xs text-slate-400">Open</label>
                        <input type="time" value={fmt(h.open_time)}
                          onChange={e => updateHour(day, "open_time", e.target.value)}
                          className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                        <span className="text-slate-300 text-xs">—</span>
                        <label className="text-xs text-slate-400">Close</label>
                        <input type="time" value={fmt(h.close_time)}
                          onChange={e => updateHour(day, "close_time", e.target.value)}
                          className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                      </div>
                      <button onClick={() => toggleDay(day)} className="text-slate-300 hover:text-red-400 transition-colors">
                        <Ico.Trash />
                      </button>
                    </div>
                  );
                })}
                {weeklyHours.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-4">No days selected. Toggle days above to set hours.</p>
                )}
              </div>
            </div>



            {/* Holiday Schedule */}
            <div className="px-6 py-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400"><Ico.Calendar /></span>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Holiday Schedule</p>
                </div>
                <button onClick={addHoliday}
                  className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-all">
                  <Ico.Plus /> Add Holiday
                </button>
              </div>

              <div className="space-y-3">

                {holidays.map((h, i) => (

                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                    <input type="date" value={h.date} min={today}
                      onChange={e => updateHoliday(i, "date", e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white text-slate-700" />
                    <label className="text-xs text-slate-400 shrink-0">From</label>
                    <input type="time" value={fmt(h.start_time)}
                      onChange={e => updateHoliday(i, "start_time", e.target.value.replace(":", ""))}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                    <label className="text-xs text-slate-400 shrink-0">To</label>
                    <input type="time" value={fmt(h.end_time)}
                      onChange={e => updateHoliday(i, "end_time", e.target.value.replace(":", ""))}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white" />
                    <button onClick={() => removeHoliday(i)} className="text-slate-300 hover:text-red-400 transition-colors ml-auto">
                      <Ico.Trash />
                    </button>
                  </div>
                ))}
                {holidays.length === 0 && (
                  <p className="text-xs text-slate-400 text-center py-3">No holidays added.</p>
                )}
              </div>
            </div>

          </div>
        </Card>


        {/* Save Button */}
        <div className="flex justify-end pb-4">
          <button
            onClick={saveSettings}
            className="flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
          >
            <Ico.Check /> Save Settings
          </button>
        </div>

      </div>



    </div>
  );
};

export default CallSettings;